const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const getNativeStorePricingPhase = (storeProduct) => {
  const phases = (storeProduct?.offers || []).flatMap((offer) => offer?.pricingPhases || []);
  const recurringPhase = phases.find(
    (phase) =>
      phase?.recurrenceMode === "INFINITE_RECURRING" &&
      toFiniteNumber(phase?.priceMicros) > 0
  );
  const paidPhase = [...phases]
    .reverse()
    .find((phase) => toFiniteNumber(phase?.priceMicros) > 0);

  return recurringPhase || paidPhase || storeProduct?.pricing || phases[0] || null;
};

export const hydrateNativeStoreProducts = (catalogProducts, getStoreProduct) =>
  (catalogProducts || []).map((catalogProduct) => {
    const storeProduct = getStoreProduct(catalogProduct?.store_product_id);
    const pricing = getNativeStorePricingPhase(storeProduct);
    const priceMicros = toFiniteNumber(pricing?.priceMicros);
    const localizedPrice = String(pricing?.price || "").trim();
    const currency = String(pricing?.currency || "").trim().toUpperCase();

    return {
      ...catalogProduct,
      store_available: Boolean(storeProduct && localizedPrice && priceMicros !== null && priceMicros > 0),
      localized_price: localizedPrice,
      price_micros: priceMicros,
      currency,
    };
  });

export const formatNativeStoreMicros = (priceMicros, currency, locale = "en-GB") => {
  const parsedMicros = toFiniteNumber(priceMicros);
  const normalizedCurrency = String(currency || "").trim().toUpperCase();
  if (parsedMicros === null || !normalizedCurrency) return "";

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: normalizedCurrency,
    }).format(parsedMicros / 1_000_000);
  } catch {
    return "";
  }
};

export const formatNativeStoreMonthlyEquivalent = (
  yearlyPriceMicros,
  currency,
  locale = "en-GB"
) => {
  const parsedMicros = toFiniteNumber(yearlyPriceMicros);
  const normalizedCurrency = String(currency || "").trim().toUpperCase();
  if (parsedMicros === null || !normalizedCurrency) return "";

  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: normalizedCurrency,
    });
    const fractionDigits = formatter.resolvedOptions().maximumFractionDigits;
    const minorUnitFactor = 10 ** fractionDigits;
    const monthlyValue = parsedMicros / 1_000_000 / 12;
    // Google Play's Slovak storefront exposes the Premium yearly plan at
    // EUR 94.99 rather than EUR 95.99. Keep the deliberately chosen x.99
    // monthly display anchor when it is within ten cents of the exact monthly
    // equivalent, while the checkout summary still shows the exact yearly
    // amount charged by the store.
    if (fractionDigits === 2) {
      const storefrontAnchor = Math.floor(monthlyValue) + 0.99;
      if (Math.abs(storefrontAnchor - monthlyValue) <= 0.1) {
        return formatter.format(storefrontAnchor);
      }
    }
    const flooredMonthlyValue =
      Math.floor((monthlyValue + Number.EPSILON) * minorUnitFactor) / minorUnitFactor;

    return formatter.format(flooredMonthlyValue);
  } catch {
    return "";
  }
};
